// Helpers for recognising when a write was rejected because the user is a
// read-only QA auditor. Read-only is enforced in the database two ways:
//   1. The block_qa_writes trigger on every business table raises 'read_only_qa'
//      (covers all direct writes and SECURITY DEFINER RPCs that reach a write).
//   2. Manager-only write RPCs raise 'Access denied: manager role required'
//      BEFORE reaching the table — these live behind manager-gated UI, so the
//      only non-managers who can reach them are QA auditors.
//
// We deliberately do NOT treat a bare 42501 / "row-level security" rejection as
// read-only: a regular (basic) user editing e.g. a locked timesheet legitimately
// hits 42501, and must not be told they have "read-only QA access". QA write
// attempts always carry one of the two signals above.
//
// A blocked write reaches the frontend as either a thrown/returned error, or as
// an RPC data payload { success: false, error: 'read_only_qa' }.

const READ_ONLY_MESSAGE =
  'You have read-only (QA) access and can’t make changes.';

interface MaybePostgrestError {
  code?: string;
  message?: string;
  details?: string;
  hint?: string;
  error?: string;
}

/** True if the given error object represents a QA read-only rejection. */
export const isReadOnlyError = (error: unknown): boolean => {
  if (!error) return false;

  const e = error as MaybePostgrestError;
  const haystack = [e.message, e.details, e.hint, e.error]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  if (haystack.includes('read_only_qa')) return true;
  if (haystack.includes('manager role required')) return true;

  return false;
};

/**
 * True if an RPC's returned data payload signals a read-only rejection
 * (e.g. { success: false, error: 'read_only_qa' }).
 */
export const isReadOnlyResult = (data: unknown): boolean => {
  if (!data || typeof data !== 'object') return false;
  const d = data as { success?: boolean; error?: string };
  return d.success === false && (d.error ?? '').toLowerCase().includes('read_only_qa');
};

/**
 * Returns the friendly read-only message if the error/result is a QA rejection,
 * otherwise null. Use in catch/error branches to show a useful message instead
 * of a raw database error.
 */
export const getReadOnlyMessage = (errorOrResult: unknown): string | null => {
  if (isReadOnlyError(errorOrResult) || isReadOnlyResult(errorOrResult)) {
    return READ_ONLY_MESSAGE;
  }
  return null;
};

export const READ_ONLY_ACCESS_MESSAGE = READ_ONLY_MESSAGE;

/** Event name used to surface the global read-only toast. */
export const READ_ONLY_EVENT = 'qa-read-only';

/**
 * Dispatch the global read-only toast (listened to by <ReadOnlyToast/> in the
 * app layout). Call directly when you knowingly short-circuit a write for a QA
 * user, or pass an error/result to only fire when it is actually a QA rejection.
 */
export const notifyReadOnly = (errorOrResult?: unknown): void => {
  if (typeof window === 'undefined') return;
  if (errorOrResult !== undefined && !getReadOnlyMessage(errorOrResult)) return;
  window.dispatchEvent(new CustomEvent(READ_ONLY_EVENT, { detail: READ_ONLY_MESSAGE }));
};

/**
 * Surface a persistent toast for a failed save the user might otherwise never
 * see (e.g. an autosave flushed while a modal closes, whose inline error badge
 * unmounts immediately). QA/manager rejections are skipped — notifyReadOnly
 * already shows their specific message.
 */
export const notifySaveFailure = (error: unknown, message: string): void => {
  if (typeof window === 'undefined') return;
  if (error != null && getReadOnlyMessage(error)) return;
  window.dispatchEvent(new CustomEvent(READ_ONLY_EVENT, { detail: message }));
};

/**
 * The message to show for a failed write. Supabase returns a plain
 * PostgrestError object, not an Error, so `err instanceof Error` misses it
 * and every refusal the database words for the user (a locked week, a lunch
 * that covers an entry) would fall back to a generic line.
 */
export const errorMessage = (error: unknown, fallback: string): string => {
  const message = (error as MaybePostgrestError | null)?.message;
  return typeof message === 'string' && message.trim() ? message : fallback;
};
